import { FC, useEffect, useMemo, useState } from 'react';

import { RootState } from 'store/rootReducer';

import { useAppSelector } from 'hooks/defaultStoreHooks';

import AutocompleteSelect, {
  IRaaFilteredSelectItems,
  ISelectableItem
} from 'components/autocompleteSelect/AutocompleteSelect';
import { SearchBebyggelseFormValues } from 'components/search/bebyggelseobjekt/searchBebyggelseobjektSchema';
import { SearchAktiviteterFormValues } from 'components/search/inventering/searchInventeringSchema';
import TagList from 'components/tagList/TagList';

import { Lan, LanKommun } from 'models';

import { useFormikContext } from 'formik';

export const getAllOmraden = (
  kommunkodParam: string | null,
  lankodParam: string | null,
  geo: {
    kommun: Array<LanKommun>;
    lan: Array<Lan>;
  }
) => {
  let allaOmraden: string[] = [];

  if (kommunkodParam) {
    const kommunkoder = kommunkodParam.split(',');
    const kommuner = geo.kommun.filter((k) => kommunkoder.some((kk) => kk === k.kommun_kod)).map((k) => k.kommun_namn);
    allaOmraden = allaOmraden.concat(kommuner);
  }

  if (lankodParam) {
    const lankoder = lankodParam.split(',');
    const lan = geo.kommun.filter((l) => lankoder.some((lk) => lk === l.lan_kod)).map((l) => l.lan_namn);
    allaOmraden = allaOmraden.concat(lan);
  }

  return allaOmraden;
};

export const getLanAndKommunValues = (kommuner: LanKommun[], lan: Lan[]) => {
  const lanItems: IRaaFilteredSelectItems = {
    items: lan.map((value: Lan) => ({
      name: value.lan_namn,
      relatesToParentName: '',
      id: value.lan_kod,
      type: 'LAN'
    })),
    name: ['LÄN'],
    type: 'LAN'
  };

  const kommunItems: IRaaFilteredSelectItems = {
    items: kommuner.map((value: LanKommun) => ({
      name: value.kommun_namn,
      relatesToParentName: value.lan_namn,
      id: value.kommun_kod,
      type: 'KOMMUN'
    })),
    name: ['KOMMUN', 'LÄN'],
    type: 'KOMMUN'
  };

  return { lanItems, kommunItems };
};

export const LanKommunSelectWithTagList: FC<{
  disabled: boolean;
}> = ({ disabled }) => {
  const { setFieldValue, values, setFieldTouched } = useFormikContext<SearchBebyggelseFormValues>();
  const { data: geo } = useAppSelector((state: RootState) => state.geo);
  const { lanItems, kommunItems } = useMemo(() => getLanAndKommunValues(geo.kommun, geo.lan), [geo.kommun, geo.lan]);
  const [omraden, setOmraden] = useState<Array<IRaaFilteredSelectItems>>([lanItems, kommunItems]);

  const handleOnSelect = async (itemToAddOrRemove: ISelectableItem, type: 'ADD' | 'REMOVE') => {
    if (itemToAddOrRemove.type === 'KOMMUN') {
      if (type === 'ADD') {
        await setFieldValue('kommun_kod', [...values.kommun_kod, itemToAddOrRemove.id]);
      } else if (type === 'REMOVE') {
        await setFieldValue('kommun_kod', [...values.kommun_kod.filter((item) => item !== itemToAddOrRemove.id)]);
      }

      void setFieldTouched('kommun_kod', true);
    }

    if (itemToAddOrRemove.type === 'LAN') {
      if (type === 'ADD') {
        await setFieldValue('lan_kod', [...values.lan_kod, itemToAddOrRemove.id]);
      } else if (type === 'REMOVE') {
        await setFieldValue('lan_kod', [...values.lan_kod.filter((item) => item !== itemToAddOrRemove.id)]);
      }

      await setFieldTouched('lan_kod', true, false);
    }
  };

  const getFilteredAndCombinedSelectedLanAndKommuner = () => {
    const selectedKommuner = kommunItems.items.filter((kommun) => values.kommun_kod.some((kod) => kod === kommun.id));
    const selectedLan = lanItems.items.filter((lan) => values.lan_kod.some((kod) => kod === lan.id));
    return selectedKommuner.concat(selectedLan);
  };

  useEffect(() => {
    const filteredKommuner = kommunItems.items.filter(
      (selectableItem) => !values.kommun_kod.some((kommunkod) => kommunkod === selectableItem.id)
    );

    const filteredLan = lanItems.items.filter(
      (selectableItem) => !values.lan_kod.some((lankod) => lankod === selectableItem.id)
    );

    setOmraden([
      {
        ...lanItems,
        items: filteredLan
      },
      {
        ...kommunItems,
        items: filteredKommuner
      }
    ]);
  }, [values.lan_kod, values.kommun_kod, lanItems, kommunItems]);

  return (
    <>
      <label className="raa-label tw-flex" htmlFor="lanKommun">
        Område (län eller kommun)
      </label>
      <AutocompleteSelect
        items={omraden}
        onSelect={(item) => handleOnSelect(item, 'ADD')}
        placeholderText="Sök efter län eller kommun"
        showButton={false}
        inputElementId="lanKommun"
        disabled={disabled}
      />

      <div className="tw-mt-2">
        <TagList
          list={getFilteredAndCombinedSelectedLanAndKommuner()}
          leftColumn={(omrade: ISelectableItem) => {
            const typeAsString = omrade.type === 'LAN' ? 'Län' : omrade.type === 'KOMMUN' ? 'Kommun' : undefined;
            return `${typeAsString && typeAsString + ':'} ${omrade.name}`;
          }}
          onRemoveItem={(item) => handleOnSelect(item, 'REMOVE')}
        />
      </div>
    </>
  );
};

export const getStringArray = (paramValue: string | null): string[] | undefined => {
  return paramValue && paramValue.length ? paramValue.split(',') : undefined;
};

export const buildQueryString = (values: SearchBebyggelseFormValues | SearchAktiviteterFormValues) => {
  return Object.entries(values)
    .filter(
      ([, value]) =>
        (value !== undefined && value.length > 0) ||
        (typeof value === 'object' && 'items' in value && value.items.length > 0)
    )
    .map(
      ([key, value]) =>
        key +
        '=' +
        (typeof value === 'object' && 'items' in value
          ? (value.items as Array<ISelectableItem>).map((item) => item.id)
          : value)
    )
    .join('&');
};
